import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Checkbox from '../../../components/ui/Checkbox';
import { useAuth } from '../../../contexts/AuthContext';
import { getUserPreferences, updateUserPreferences } from '../../../services/notificationService';

const SettingsModal = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [preferences, setPreferences] = useState({
    emailNotifications: true,
    pushNotifications: true,
    smsNotifications: false,
    bookingConfirmations: true,
    paymentAlerts: true,
    tripReminders: true
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (user?.id) {
      loadPreferences();
    }
  }, [user?.id]);

  const loadPreferences = async () => {
    setLoading(true);
    const prefs = await getUserPreferences(user?.id);
    if (prefs) {
      setPreferences(prefs);
    }
    setLoading(false);
  };

  const handleSave = async () => {
    setSaving(true);
    await updateUserPreferences(user?.id, preferences);
    setSaving(false);
    onClose();
  };

  const handleToggle = (key) => {
    setPreferences((prev) => ({ ...prev, [key]: !prev?.[key] }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="Settings" size={20} className="text-primary" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-foreground">Notification Preferences</h2>
              <p className="text-sm text-muted-foreground">Manage how you receive notifications</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-lg hover:bg-muted flex items-center justify-center transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-180px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Delivery Methods */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Delivery Methods</h3>
                <div className="space-y-3">
                  <label className="flex items-center justify-between p-4 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted transition-colors">
                    <div className="flex items-center gap-3">
                      <Icon name="Mail" size={20} className="text-muted-foreground" />
                      <div>
                        <p className="font-medium text-foreground">Email Notifications</p>
                        <p className="text-sm text-muted-foreground">Receive notifications via email</p>
                      </div>
                    </div>
                    <Checkbox
                      checked={preferences?.emailNotifications}
                      onChange={() => handleToggle('emailNotifications')}
                    />
                  </label>

                  <label className="flex items-center justify-between p-4 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted transition-colors">
                    <div className="flex items-center gap-3">
                      <Icon name="Bell" size={20} className="text-muted-foreground" />
                      <div>
                        <p className="font-medium text-foreground">Push Notifications</p>
                        <p className="text-sm text-muted-foreground">Receive browser push notifications</p>
                      </div>
                    </div>
                    <Checkbox
                      checked={preferences?.pushNotifications}
                      onChange={() => handleToggle('pushNotifications')}
                    />
                  </label>

                  <label className="flex items-center justify-between p-4 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted transition-colors">
                    <div className="flex items-center gap-3">
                      <Icon name="MessageSquare" size={20} className="text-muted-foreground" />
                      <div>
                        <p className="font-medium text-foreground">SMS Notifications</p>
                        <p className="text-sm text-muted-foreground">Receive notifications via text message</p>
                      </div>
                    </div>
                    <Checkbox
                      checked={preferences?.smsNotifications}
                      onChange={() => handleToggle('smsNotifications')}
                    />
                  </label>
                </div>
              </div>

              {/* Notification Types */}
              <div>
                <h3 className="text-lg font-semibold text-foreground mb-4">Notification Types</h3>
                <div className="space-y-3">
                  <label className="flex items-center justify-between p-4 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted transition-colors">
                    <div className="flex items-center gap-3">
                      <Icon name="CheckCircle" size={20} className="text-green-600" />
                      <div>
                        <p className="font-medium text-foreground">Booking Confirmations</p>
                        <p className="text-sm text-muted-foreground">Get notified when bookings are confirmed</p>
                      </div>
                    </div>
                    <Checkbox
                      checked={preferences?.bookingConfirmations}
                      onChange={() => handleToggle('bookingConfirmations')}
                    />
                  </label>

                  <label className="flex items-center justify-between p-4 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted transition-colors">
                    <div className="flex items-center gap-3">
                      <Icon name="CreditCard" size={20} className="text-orange-600" />
                      <div>
                        <p className="font-medium text-foreground">Payment Alerts</p>
                        <p className="text-sm text-muted-foreground">Receive payment status updates</p>
                      </div>
                    </div>
                    <Checkbox
                      checked={preferences?.paymentAlerts}
                      onChange={() => handleToggle('paymentAlerts')}
                    />
                  </label>

                  <label className="flex items-center justify-between p-4 bg-muted/50 rounded-lg cursor-pointer hover:bg-muted transition-colors">
                    <div className="flex items-center gap-3">
                      <Icon name="Calendar" size={20} className="text-blue-600" />
                      <div>
                        <p className="font-medium text-foreground">Trip Reminders</p>
                        <p className="text-sm text-muted-foreground">Get reminders about upcoming trips</p>
                      </div>
                    </div>
                    <Checkbox
                      checked={preferences?.tripReminders}
                      onChange={() => handleToggle('tripReminders')}
                    />
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave} loading={saving}>
            Save Preferences
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;